import { Component, type ReactNode } from "react";
import { withSentry } from "@/lib/sentry-lazy";

type FallbackRender = (args: { reset: () => void; label: string }) => ReactNode;

type Props = {
  children: ReactNode;
  label?: string;
  /** Optional custom fallback. Receives a `reset()` that re-mounts children. */
  fallback?: ReactNode | FallbackRender;
};
type State = { hasError: boolean; nonce: number };

/**
 * Local error boundary for a single section (PDP or homepage rail).
 * A failing lazy chunk or render error is contained here so the rest of
 * the page stays usable. Optional `fallback` prop enables actionable
 * retry UIs (see RailErrorFallback).
 */
export class SectionErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, nonce: 0 };

  static getDerivedStateFromError(): Partial<State> {
    return { hasError: true };
  }

  reset = () => {
    this.setState((s) => ({ hasError: false, nonce: s.nonce + 1 }));
  };

  componentDidCatch(error: unknown, info: { componentStack?: string | null }) {
    const label = this.props.label ?? "unknown";
    const err = error instanceof Error ? error : new Error(String(error));
    const isChunkLoad =
      /ChunkLoadError|Loading chunk|Failed to fetch dynamically imported module|Importing a module script failed/i.test(
        err.message ?? "",
      ) || err.name === "ChunkLoadError";

    try {
      withSentry((Sentry) => {
        Sentry.withScope((scope) => {
          scope.setTag("boundary", "section");
          scope.setTag("section", label);
          scope.setTag("chunk_load_error", isChunkLoad ? "true" : "false");
          scope.setContext("react", { componentStack: info?.componentStack ?? null });
          scope.setFingerprint(["section-error-boundary", label, isChunkLoad ? "chunk" : "render"]);
          Sentry.captureException(err);
        });
      });
    } catch {
      // Never let telemetry break the fallback UI.
    }

    if (typeof console !== "undefined") {
      console.warn(`[Section "${label}"] failed to load`, error);
    }
  }

  render() {
    if (this.state.hasError) {
      const { fallback, label = "unknown" } = this.props;
      if (typeof fallback === "function") {
        return (fallback as FallbackRender)({ reset: this.reset, label });
      }
      if (fallback !== undefined) return fallback;
      return (
        <div
          role="status"
          className="mt-12 border-t border-border-subtle pt-8 text-sm text-muted-foreground"
        >
          This section couldn&apos;t load. Refresh the page to try again.
        </div>
      );
    }
    // `nonce` in the key re-mounts children after reset() so a transient
    // chunk-load failure recovers without a full page reload.
    return <div key={this.state.nonce}>{this.props.children}</div>;
  }
}
